import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Phone } from 'lucide-react';
import { getGeoLocation, submitLead } from '../services/api';
import { countryCodes } from '../data/countryCodes';

const ContactPage = () => {
  useEffect(() => {
    document.title = 'Contact Us | Federal IP Office - Get Legal Help Today';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Federal IP Office for a legal consultation. Reach our experienced attorneys for corporate law, IP protection, and business legal services.');
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryDialCode: '1',
    country: 'US',
    phoneNumber: '',
    description: '',
  });
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const matchedCountry = countryCodes.find(c => c.countryCode === data.data.countryCode);
        if (matchedCountry) {
          setFormData((prev) => ({
            ...prev,
            country: matchedCountry.countryCode,
            countryDialCode: matchedCountry.code.replace('+', ''),
          }));
        }
      }
    };
    fetchGeoData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        countryDialCode: parseInt(formData.countryDialCode),
        country: formData.country,
        phoneNumber: formData.phoneNumber,
        ipAddress: geoData?.data?.ipAddress || '',
        description: formData.description,
        pageUrl: window.location.href,
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        geoLocation: geoData ? JSON.stringify(geoData) : '',
        packageTitle: '',
        packageAmount: 0,
      };

      const response = await submitLead(payload);

      if (response.status === 200 && response.data.data.customerId) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${response.data.data.customerId}`;
        }, 1500);
      }
    } catch (err) {
      if (err.response) {
        const { status, data } = err.response;
        if (status === 401) {
          setError(data.message || 'Unauthorized access.');
        } else if (status === 400) {
          setError(data.errorMessage || 'Invalid data provided.');
        } else {
          setError('An error occurred. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5668772/pexels-photo-5668772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/75"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">
              Contact Us
            </div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6" data-testid="contact-heading">
              Get In Touch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Have a legal question or need consultation? Reach out to our team and we'll get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] mb-8">
                Contact Information
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CA9A67] flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">Office Address</h3>
                    <p className="text-[#2E2E2E]/80">550 W B St, San Diego, CA 92101</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CA9A67] flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">Email</h3>
                    <a href="mailto:Info@federalipoffice.com" className="text-[#2E2E2E]/80 hover:text-[#2C3E50]">
                      Info@federalipoffice.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#CA9A67] flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3E50] mb-1">Phone</h3>
                    <a href="tel:+18335187356" className="text-[#2E2E2E]/80 hover:text-[#2C3E50]">
                      +1 (833) 518-7356 
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8F9FA] p-8">
                <h3 className="font-semibold text-[#2C3E50] mb-4">Office Hours</h3>
                <div className="space-y-2 text-sm text-[#2E2E2E]/80">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] mb-8">
                Send Us a Message
              </h2>

              {success ? (
                <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="success-message">
                  <div className="text-green-600 font-semibold text-lg mb-2">Thank You!</div>
                  <p className="text-[#2E2E2E]/70">Your message has been received. Redirecting you now...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-4 py-3 outline-none"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-4 py-3 outline-none"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                        Country Code *
                      </label>
                      <select
                        name="countryDialCode"
                        value={formData.countryDialCode}
                        onChange={(e) => {
                          const selectedCode = countryCodes.find(c => c.code.replace('+', '') === e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            countryDialCode: e.target.value,
                            country: selectedCode ? selectedCode.countryCode : prev.country,
                          }));
                        }}
                        required
                        className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-4 py-3 outline-none"
                        data-testid="contact-country-code"
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code.replace('+', '')}>
                            {country.code} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        required
                        maxLength="10"
                        placeholder="1234567890"
                        className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-4 py-3 outline-none"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2E2E2E] mb-2">
                      Message *
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows="6"
                      placeholder="Tell us about your legal needs..."
                      className="w-full bg-white border border-[#2C3E50]/20 focus:border-[#2C3E50] focus:ring-1 focus:ring-[#2C3E50] px-4 py-3 outline-none resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="contact-error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
